import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { siteNav } from "@/mocks/site";
import { getSuggestions } from "@/lib/search";

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [suggestions, setSuggestions] = useState<{ label: string; type: string; path: string }[]>([]);
  const [openSuggest, setOpenSuggest] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpenSuggest(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (value: string) => {
    setKeyword(value);
    const list = getSuggestions(value);
    setSuggestions(list);
    setOpenSuggest(list.length > 0);
  };

  const submitSearch = (value: string) => {
    const kw = value.trim();
    if (!kw) return;
    setOpenSuggest(false);
    setMobileOpen(false);
    navigate(`/search?q=${encodeURIComponent(kw)}`);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" onClick={() => setMobileOpen(false)}>
            <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading text-xl font-bold">
              幂
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading text-lg font-bold text-foreground-950">杨幂资讯站</span>
              <span className="text-[11px] tracking-widest text-primary-600 font-label">YANG MI INFO</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {siteNav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `text-sm font-label transition-colors whitespace-nowrap ${
                    isActive ? "text-primary-600 font-semibold" : "text-foreground-700 hover:text-primary-600"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block relative w-72" ref={boxRef}>
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4 h-10">
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                value={keyword}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={() => keyword && setOpenSuggest(suggestions.length > 0)}
                onKeyDown={(e) => e.key === "Enter" && submitSearch(keyword)}
                placeholder="搜索作品或资讯"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  aria-label="搜索"
                  onClick={() => submitSearch(keyword)}
                  className="w-6 h-6 flex items-center justify-center text-primary-600 cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-base" />
                </button>
              )}
            </div>
            {openSuggest && (
              <div className="absolute top-12 left-0 right-0 bg-background-50 border border-background-200 rounded-lg overflow-hidden py-1">
                {suggestions.map((s) => (
                  <button
                    key={s.path}
                    onClick={() => {
                      setOpenSuggest(false);
                      navigate(s.path);
                    }}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-background-100 cursor-pointer"
                  >
                    <span className="text-sm text-foreground-800 truncate">{s.label}</span>
                    <span className="text-[11px] text-secondary-600 shrink-0 ml-2">{s.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            aria-label="菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 animate-fade-up">
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4 h-11 mb-3">
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                value={keyword}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitSearch(keyword)}
                placeholder="搜索作品或资讯"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
              />
              <button
                aria-label="搜索"
                onClick={() => submitSearch(keyword)}
                className="w-6 h-6 flex items-center justify-center text-primary-600 cursor-pointer"
              >
                <i className="ri-search-line text-base" />
              </button>
            </div>
            <nav className="flex flex-col">
              {siteNav.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `py-3 px-2 text-sm font-label border-b border-background-100 ${
                      isActive ? "text-primary-600 font-semibold" : "text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}